import React from 'react';
import FormField from './FormField';

const SIZE_CLASSES = {
  sm: 'px-3 py-2',
  md: 'px-3 py-2.5',
  lg: 'px-4 py-3',
} as const;

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      size = 'md',
      className = '',
      id,
      ...inputProps
    },
    ref,
  ) => {
    const inputId = id ?? (label ? `textfield-${label.replace(/\s/g, '-').toLowerCase()}` : undefined);
    const sizeClasses = SIZE_CLASSES[size];
    const hasLeftIcon = Boolean(leftIcon);
    const hasRightIcon = Boolean(rightIcon);

    const inputClasses = [
      'w-full rounded-xl bg-surface-primary border text-content text-sm placeholder-content-muted outline-none focus:border-brand/40 transition-all',
      sizeClasses,
      hasLeftIcon && 'pl-9',
      hasRightIcon && 'pr-9',
      error ? 'border-status-error/40' : 'border-subtle',
    ]
      .filter(Boolean)
      .join(' ');

    const input = (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-content-faint pointer-events-none">
            {leftIcon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          {...inputProps}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-content-faint pointer-events-none">
            {rightIcon}
          </div>
        )}
      </div>
    );

    if (label || error || hint) {
      return (
        <FormField
          label={label}
          error={error}
          hint={hint}
          required={inputProps.required}
          htmlFor={inputId}
          className={className}
        >
          {input}
        </FormField>
      );
    }

    return <div className={className}>{input}</div>;
  },
);

TextField.displayName = 'TextField';

export default TextField;
